import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, input, model, output, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import dayjs from 'dayjs/esm/index';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import { FtDateRangePickerComponent } from 'apps/account/src/account-shared/legacy-shims/daterange/ft-date-range-picker.component';
import { AdvocatesApiService } from '../services/advocates-api.service';

/**
 * Native port of the v1 `people.details.guestInfo` edit modal (details.html) + `vm.save`
 * (details.controller.js). Edits guest name / stay dates / profile image / email and PUTs via
 * AdvocatesApiService.save. Opened from the people-details body's "Edit guest info" button;
 * emits the updated advocate so the shell header refreshes (v1 `updateSheetTitle`).
 *
 * Form fields are seeded from `advocate` (v1 `ft-snapshot`) and reset on cancel.
 */
@Component({
    selector: 'ft-edit-guest-details-ng',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        FtModalComponent,
        FtModalChromeComponent,
        FtDateRangePickerComponent,
    ],
    templateUrl: './edit-guest-details.component.html',
    styleUrls: ['./edit-guest-details.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FtEditGuestDetailsComponent {
    readonly advocate = input<any>();
    readonly open = model(false);
    readonly saved = output<any>();

    protected first = '';
    protected last = '';
    protected email = '';
    protected startDate: any;
    protected endDate: any;
    protected removingProfileImage = false;
    protected profileImageUrl = '';
    protected hasStayDates = false;

    private readonly api = inject(AdvocatesApiService);
    private readonly spaces = inject(SpacesService);

    constructor() {
        // Seed the form whenever the advocate loads/changes, so the fields (and the date picker
        // read on its ngAfterViewInit) are ready before the modal opens.
        effect(() => {
            const advocate = this.advocate();
            if (advocate) this.seed(advocate);
        });
    }

    protected save(): void {
        const advocate = this.advocate();
        const guestUUID = advocate?.guestUUID;
        const propertyUuid = this.spaces.getPropertyUuid()!;
        const payload: any = {
            FirstName: this.first,
            LastName: this.last,
            Email: this.email,
            ProfileImageUrl: this.removingProfileImage ? '' : this.profileImageUrl,
            PersonUUID: guestUUID,
        };
        if (this.hasStayDates && this.startDate && this.endDate) {
            payload.StartDate = this.startDate;
            payload.EndDate = this.endDate;
        }
        this.api.save(propertyUuid, guestUUID, payload).subscribe({
            next: () => {
                const updated = {
                    ...advocate,
                    first: this.first,
                    last: this.last,
                    email: this.email,
                    profileImageUrl: this.removingProfileImage ? '' : this.profileImageUrl,
                    epochStartDate:
                        this.hasStayDates && this.startDate ? this.startDate.valueOf() : advocate?.epochStartDate,
                    epochEndDate:
                        this.hasStayDates && this.endDate ? this.endDate.valueOf() : advocate?.epochEndDate,
                };
                this.saved.emit(updated);
                this.open.set(false);
            },
        });
    }

    protected cancel(): void {
        const advocate = this.advocate();
        if (advocate) this.seed(advocate); // v1 saveCanceled: revert edits
        this.open.set(false);
    }

    protected removeImage(): void {
        this.removingProfileImage = true;
    }

    private seed(advocate: any): void {
        this.first = advocate.first ?? '';
        this.last = advocate.last ?? '';
        this.email = advocate.email ?? '';
        this.profileImageUrl = advocate.profileImageUrl ?? '';
        this.removingProfileImage = false;
        // epochStartDate/epochEndDate arrive as STRINGS (templates pipe them via parseValue:'number'),
        // so coerce to a numeric ms timestamp — dayjs('<digits>') would parse it as a date string and
        // silently yield a date in 1713.
        this.hasStayDates = +advocate.epochStartDate > 0;
        if (this.hasStayDates) {
            this.startDate = dayjs(+advocate.epochStartDate);
            this.endDate = dayjs(+advocate.epochEndDate);
        }
    }
}
